import React, {useState, useRef, useMemo, useEffect} from 'react';
import {Box, InputAdornment, IconButton} from '@mui/material';
import {t} from 'i18next';
import Search from '@mui/icons-material/Search';
import Cancel from '@mui/icons-material/Cancel';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../../common/arr-menu/ArrMenu';
import {TranscriptSortKey} from '../../AiInvestigateUtils';
import {ReactComponent as SortIcon} from '../../../../../assets/images/sort-v2.svg';
import {ReactComponent as FilterIcon} from '../../../../../assets/images/filter.svg';
import './SearchAndFilterBar.scss';
import {ReactComponent as FilterApplied} from '../../../../../assets/images/as-filter-applied.svg';

export interface SearchAndFilterBarProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSortChange?: (sortKey: TranscriptSortKey) => void;
  onFilterClick?: () => void;
  searchPlaceholder?: string;
  selectedSortKey?: TranscriptSortKey;
  isFiltersApplied?: boolean;
  isInitialState?: boolean;
}

const SearchAndFilterBar: React.FC<SearchAndFilterBarProps> = ({
  searchValue = '',
  onSearchChange,
  onSortChange,
  onFilterClick,
  searchPlaceholder,
  selectedSortKey,
  isFiltersApplied = false,
  isInitialState = true,
}) => {
  const defaultPlaceholder =
    searchPlaceholder || t('ai.investigate.transcriptPanel.searchPlaceholder');
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const [searchPerformed, setSearchPerformed] = useState(false);

  useEffect(() => {
    setLocalSearchValue(searchValue);
    if (searchValue === '') setSearchPerformed(false);
  }, [searchValue]);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<ArrMenuHandle | null>(null);

  const sortOptions: CustomMenuItem[] = useMemo(
    () => [
      {
        id: TranscriptSortKey.ModifiedNewest,
        label: t('ai.investigate.transcriptPanel.sortOptions.modifiedNewest'),
        value: TranscriptSortKey.ModifiedNewest,
        selected: selectedSortKey === TranscriptSortKey.ModifiedNewest,
      },
      {
        id: TranscriptSortKey.ModifiedOldest,
        label: t('ai.investigate.transcriptPanel.sortOptions.modifiedOldest'),
        value: TranscriptSortKey.ModifiedOldest,
        selected: selectedSortKey === TranscriptSortKey.ModifiedOldest,
      },
      {
        id: TranscriptSortKey.CreatedNewest,
        label: t('ai.investigate.transcriptPanel.sortOptions.createdNewest'),
        value: TranscriptSortKey.CreatedNewest,
        selected: selectedSortKey === TranscriptSortKey.CreatedNewest,
      },
      {
        id: TranscriptSortKey.CreatedOldest,
        label: t('ai.investigate.transcriptPanel.sortOptions.createdOldest'),
        value: TranscriptSortKey.CreatedOldest,
        selected: selectedSortKey === TranscriptSortKey.CreatedOldest,
      },
    ],
    [selectedSortKey, t]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchValue(value);
    if (searchPerformed) {
      setSearchPerformed(false);
    }
    // Do not notify parent when clearing by typing; user must press Enter to load full list
  };

  const handleSearch = () => {
    if (localSearchValue.trim()) {
      setSearchPerformed(true);
      onSearchChange?.(localSearchValue);
    } else {
      onSearchChange?.('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    setLocalSearchValue('');
    setSearchPerformed(false);
    onSearchChange?.('');
  };

  const handleSearchIconClick = () => {
    handleSearch();
  };

  const handleSortClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isInitialState) return;
    setIsSortMenuOpen(!isSortMenuOpen);
    sortMenuRef.current?.open(e.currentTarget as HTMLElement);
  };

  const handleSortMenuItemClick = (item: CustomMenuItem) => {
    onSortChange?.(item.value as TranscriptSortKey);
    sortMenuRef.current?.close();
    setIsSortMenuOpen(false);
  };

  const FilterButtonIcon = ({active}: {active: boolean}) => {
    if (!active) return <FilterIcon />;
    return (
      <Box className="filter-applied-icon-wrapper">
        <FilterApplied />
      </Box>
    );
  };

  return (
    <Box className="search-and-filter-bar">
      <Box className="search-and-filter-bar__search">
        <ArrTextField
          fullWidth
          id="search-transcript"
          dataTestId="saf-search-transcript"
          state={isInitialState ? 'inactive' : 'enabled'}
          placeholder={defaultPlaceholder}
          sizeVariant="medium"
          type="text"
          value={localSearchValue}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          slotProps={{
            inputLabel: {shrink: false},
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {localSearchValue && searchPerformed ? (
                    <IconButton
                      onClick={handleClear}
                      aria-label="Clear search"
                      edge="end"
                      size="small"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleClear();
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      className="clear-icon-btn"
                      data-testid="clear-button"
                    >
                      <Cancel className="clear-btn" fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={handleSearchIconClick}
                      aria-label="Search"
                      edge="end"
                      size="small"
                      disabled={!localSearchValue.trim()}
                      className="search-icon-btn"
                      data-testid="search-button"
                    >
                      <Search
                        aria-hidden="true"
                        data-testid="search-icon"
                        className="arr-textfield-icon-color"
                        fontSize="small"
                      />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          }}
          className={`search-and-filter-bar__search ${isInitialState ? 'initial-state' : ''}`}
        />
      </Box>
      <Box className="search-and-filter-bar__actions">
        <ArrIconButton
          icon={<SortIcon />}
          onClick={handleSortClick}
          ariaLabel={t('ai.investigate.transcriptPanel.sort')}
          shape="default"
          size="medium"
          testId="sort-button"
          className={`search-and-filter-bar__sort-button ${isSortMenuOpen ? 'active' : ''} ${isInitialState ? 'initial-state' : ''}`}
          disabled={isInitialState}
        />
        <ArrMenu
          ref={sortMenuRef}
          id="sort-menu"
          items={sortOptions}
          title={t('ai.investigate.transcriptPanel.sortBy')}
          direction="bottom-left"
          onItemClick={handleSortMenuItemClick}
          onClose={() => setIsSortMenuOpen(false)}
          className="search-and-filter-bar__sort-menu"
        />
        <ArrIconButton
          icon={<FilterButtonIcon active={isFiltersApplied} />}
          onClick={onFilterClick}
          ariaLabel={t('ai.investigate.transcriptPanel.filter')}
          shape="default"
          size="medium"
          testId="filter-button"
          className={`search-and-filter-bar__filter-button ${isInitialState ? 'initial-state' : ''} ${isFiltersApplied ? 'filter-state' : ''}`}
          disabled={isInitialState}
        />
      </Box>
    </Box>
  );
};

export default SearchAndFilterBar;
